import { futureSummary, historySummary } from "./teamAnalysis";
import { externalXGoalsGames } from "./externalXGoals";
import { contextualMetrics } from "./teamWorkspace";
import { guidedPool } from "./guidedTeamAnalysis";
import { historicalHomeProfile, homeAdvantage } from "./teamSettingsImpact";

// Smooth relative prior/current importance: parity at 6.5 games, ~0.45% at 20.
export function priorSeasonDecay(games) {
  return 1 / (1 + Math.exp((Math.max(0, games) - 6.5) / 2.5));
}
export function normalizeWeights(weights, keys) {
  const total = keys.reduce(
    (s, k) => s + Math.max(0, Number(weights[k]) || 0),
    0
  );
  return Object.fromEntries(
    keys.map((k) => [
      k,
      total ? (Math.max(0, Number(weights[k]) || 0) * 100) / total : 0,
    ])
  );
}
export function moveWeight(weights, key, value, keys) {
  if (!keys.includes(key)) return weights;
  if (keys.length === 1) return { [key]: 100 };
  const others = keys.filter((k) => k !== key),
    n = Math.min(100, Math.max(0, value));
  const sum = others.reduce((s, k) => s + (weights[k] || 0), 0);
  return Object.fromEntries(
    keys.map((k) => [
      k,
      k === key
        ? n
        : (100 - n) * (sum ? (weights[k] || 0) / sum : 1 / others.length),
    ])
  );
}
export function metricFor(games, id, source, question, field = null) {
  if (question === "home")
    return historicalHomeProfile(games, id, source).advantage;
  const s = historySummary(games, id, source);
  if (field)
    return field.startsWith("against") ? s.against_average : s.for_average;
  return question === "strength"
    ? s.quotient
    : question === "goals"
    ? s.goal_sum
    : null;
}
export function proposalEvidence(
  data,
  id,
  model,
  question,
  recent = 8,
  field = null
) {
  const allGames = guidedPool(data, id);
  const venue = field?.endsWith("_home")
    ? "home"
    : field?.endsWith("_away")
    ? "away"
    : "all";
  const games = allGames.filter(
      (g) => venue === "all" || Number(g[venue + "_team_id"]) === Number(id)
    ),
    savedRow = futureSummary(
      model === "result"
        ? data.result_teams?.[id]
        : data.ranking?.find((r) => r.team_id === Number(id))
    );
  const otherRow = futureSummary(
    model === "result"
      ? data.ranking?.find((r) => r.team_id === Number(id))
      : data.result_teams?.[id]
  );
  const value = (r) =>
    field
      ? r?.[field]
      : question === "home"
      ? homeAdvantage(r)
      : r?.[question === "strength" ? "total_strength" : "total_goals"];
  const record = model === "result" ? data.result_teams?.[id]?.reassessment : null;
  const basisRow = record?.baseline ? futureSummary(record.baseline) : savedRow;
  const saved = value(basisRow),
    other = value(otherRow);
  const sources = ["should", "real", "market", "recent"].map((key) => {
    const pool = key === "recent" ? games.slice(0, recent) : games,
      source = key === "recent" ? "should" : key;
    const common = pool.filter(
      (g) =>
        historySummary([g], id, source).count &&
        historySummary([g], id, model).count
    );
    const observed = metricFor(common, id, source, question, field),
      expected = metricFor(common, id, model, question, field);
    // Source levels are absolute targets. Historical expectations explain the
    // difference; adding that difference repeatedly to saved settings compounds it.
    let full = Number.isFinite(observed) ? observed : null;
    // Points moderate only an adverse real-goal correction, on the same fixtures.
    // They never fabricate goals or strengthen an already favourable correction.
    const pointGames = common.filter(
      (g) =>
        historySummary([g], id, model).outcomes &&
        historySummary([g], id, "real").outcomes
    );
    const points =
      pointGames.length === common.length && common.length
        ? {
            expected: historySummary(pointGames, id, model).outcomes.points,
            actual: historySummary(pointGames, id, "real").outcomes.points,
            count: pointGames.length,
          }
        : null;
    const rawFull = full;
    const adverse = field?.startsWith("against") ? full > saved : full < saved;
    const pointFactor =
      key === "real" &&
      model === "result" &&
      question === "strength" &&
      adverse &&
      points &&
      points.actual > points.expected
        ? Math.pow(points.expected / points.actual, 2)
        : 1;
    if (Number.isFinite(full)) full = saved + (full - saved) * pointFactor;
    const seasonCommon = games.filter(
      (g) =>
        historySummary([g], id, source).count &&
        historySummary([g], id, model).count
    );
    const identical =
      key === "recent" &&
      (games.length <= recent ||
        common.map((g) => g.id).join() ===
          seasonCommon.map((g) => g.id).join());
    return {
      key,
      rawFull,
      pointFactor,
      points,
      observed,
      expected,
      full,
      count: common.length,
      total: pool.length,
      identical,
      enabled: Number.isFinite(full) && full > 0,
      period: common.length
        ? `${common.at(-1).kickoff.slice(0, 10)} – ${common[0].kickoff.slice(
            0,
            10
          )}`
        : "",
      ids: common.map((g) => g.id),
    };
  });
  const external = externalXGoalsGames(games, data.external_xgoals_provider);
  const common = external.games.filter(
    (g) => historySummary([g], id, model).count
  );
  const observed = metricFor(common, id, "should", question, field),
    expected = metricFor(common, id, model, question, field);
  const externalFull = Number.isFinite(observed) ? observed : null;
  sources.push({
    key: "external",
    full: externalFull,
    observed,
    expected,
    count: common.length,
    total: games.length,
    enabled: Number.isFinite(externalFull) && externalFull > 0,
    provider: external.provider,
    providers: external.providers,
    ids: common.map((g) => g.id),
    desiredSelectable: true,
  });
  sources.push({
    key: "other",
    full: other,
    enabled: Number.isFinite(other) && other > 0,
    count: null,
  });
  // Actual prior-season results are an explicitly selectable, unadjusted reference.
  const previous = contextualMetrics(data, id, "previous", venue, true);
  const home = contextualMetrics(data, id, "previous", "home", true);
  const away = contextualMetrics(data, id, "previous", "away", true);
  const priorValue = field
    ? field.startsWith("against")
      ? previous.goalsAgainst
      : previous.goalsFor
    : question === "strength"
    ? previous.quotient
    : question === "goals"
    ? previous.goalTotal
    : question === "home" && home.count > 0 && away.count > 0
    ? homeAdvantage({
        for_home: home.goalsFor,
        against_home: home.goalsAgainst,
        for_away: away.goalsFor,
        against_away: away.goalsAgainst,
      })
    : null;
  const archive = data.history?.teams?.find(t => Number(t.team_id) === Number(id))?.sources?.[0];
  const transition = data.transitions?.find(t => Number(t.team_id) === Number(id));
  const previousLeague = archive?.league_id ?? transition?.from?.league_id;
  const currentLeague = data.league_id ?? transition?.to?.league_id;
  const changedLeague = previousLeague != null && currentLeague != null && Number(previousLeague) !== Number(currentLeague);
  sources.push({
    key: "previous",
    full: priorValue,
    enabled: !changedLeague && Number.isFinite(priorValue) && priorValue > 0,
    reason: changedLeague ? "PREVIOUS_LEAGUE_DIFFERENT" : null,
    count: previous.count,
    total: previous.count,
    period: previous.period,
  });
  return {
    question,
    games,
    saved,
    savedRow,
    currentSaved: value(savedRow),
    basisRow,
    other,
    sources,
    decay: priorSeasonDecay(allGames.length),
  };
}
export function effectiveWeights(evidence, weights) {
  const keys = evidence.sources.filter((s) => s.enabled).map((s) => s.key);
  return normalizeWeights(
    Object.fromEntries(
      keys.map((k) => [
        k,
        (Number(weights[k]) || 0) * (k === "previous" ? evidence.decay : 1),
      ])
    ),
    keys
  );
}
export function weightedProposal(evidence, weights, adaptation) {
  const available = evidence.sources.filter((s) => s.enabled),
    keys = available.map((s) => s.key);
  const w = effectiveWeights(evidence, weights);
  if (
    !Number.isFinite(evidence.saved) ||
    !keys.some((k) => w[k] > 0) ||
    !Number.isFinite(adaptation) ||
    adaptation < 0 ||
    adaptation > 1
  )
    return null;
  const full = available.reduce((sum, s) => sum + (s.full * w[s.key]) / 100, 0);
  // Geometric interpolation for ratios of home factors; additive for the two
  // explicitly defined strength/goal-level adjustments.
  const target =
    evidence.question === "home"
      ? evidence.saved * Math.pow(full / evidence.saved, adaptation)
      : evidence.saved + (full - evidence.saved) * adaptation;
  return Number.isFinite(target) && target > 0
    ? { target, full, delta: target - evidence.saved, weights: w }
    : null;
}

// Playful profile, not model evidence. Bounds apply to raw points, not final shares.
export function randomWeights(keys, random = Math.random) {
  return normalizeWeights(
    Object.fromEntries(keys.map((k) => [k, 5 + Math.floor(random() * 21)])),
    keys
  );
}
